import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container, Stepper, Step, StepLabel, Box, Typography, LinearProgress, useMediaQuery, useTheme,
} from "@mui/material";
import { PayStubDetails } from "assets/data/pay-stub-details";
import {
  saveCompanyInfo, saveEmployeeInfo, saveIncomeDetails, saveGeneralInfo, updateAppSettings,
} from "../../redux/actions";
import CompanyStep from "./steps/CompanyStep";
import EmployeeStep from "./steps/EmployeeStep";
import PayPeriodStep from "./steps/PayPeriodStep";
import EarningsStep from "./steps/EarningsStep";
import TemplateStep from "./steps/TemplateStep";
import ReviewStep from "./steps/ReviewStep";
import { telemetry } from "../../services/telemetry-service";
import { T } from "../../telemetry/codes";
import { isDevUnlock } from "../../utils/dev-flags";

const STEPS = ["Company", "Employee", "Pay period", "Earnings", "Template", "Review"];
const DRAFT_KEY = "paystubissa.draft.v1";
// Template selection moves to its own step in a later phase; default to the
// reference template so the existing review/preview handoff renders.
const DEFAULT_CONFIG = "/configs/template-simple/paystub-config.xml";

const PaystubWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [active, setActive] = useState(0);

  const company = useSelector((s: PayStubDetails) => s.companyInfo);
  const employee = useSelector((s: PayStubDetails) => s.employeeInfo);
  const income = useSelector((s: PayStubDetails) => s.incomeDetails);
  const general = useSelector((s: PayStubDetails) => s.generalInfo);
  const appSettings = useSelector((s: PayStubDetails) => s.appSettings);

  const hydrated = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  // Hydrate any saved draft once, and default the template so the downstream preview renders.
  useEffect(() => {
    let draftRestored = false;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        draftRestored = true;
        const d = JSON.parse(raw);
        if (d.companyInfo) dispatch(saveCompanyInfo(d.companyInfo));
        if (d.employeeInfo) dispatch(saveEmployeeInfo(d.employeeInfo));
        if (d.incomeDetails) dispatch(saveIncomeDetails(d.incomeDetails));
        if (d.generalInfo) dispatch(saveGeneralInfo(d.generalInfo));
      }
    } catch {
      /* ignore malformed draft */
    }
    if (!appSettings.configPath) {
      dispatch(updateAppSettings({ configPath: DEFAULT_CONFIG, templateId: 0 }));
    }
    // Local testing shortcut (never in production builds): `?dev=unlock` goes straight to
    // Review with print/PDF unlocked. The draft above still wins over the dev seed.
    if (isDevUnlock(location.search)) {
      dispatch(updateAppSettings({ canPrint: true }));
      setActive(STEPS.length - 1);
    }
    hydrated.current = true;
    telemetry.emit(T.WIZARD_ENTERED, { draftRestored });
    return () => {
      telemetry.emit(T.WIZARD_EXITED, { lastStep: activeRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Autosave the draft after each change (skips the initial pre-hydration render).
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ companyInfo: company, employeeInfo: employee, incomeDetails: income, generalInfo: general }),
      );
    } catch {
      /* storage may be unavailable */
    }
  }, [company, employee, income, general]);

  // Scroll to top when changing step, and report which step is now on screen.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    telemetry.emit(T.WIZARD_STEP_VIEWED, { step: active, name: STEPS[active] });
  }, [active]);

  // Each step calls onNext only after its own validation passed, so "completed" is accurate.
  const goNext = () => {
    telemetry.emit(T.WIZARD_STEP_COMPLETED, { step: active, name: STEPS[active] });
    if (active < STEPS.length - 1) setActive(a => a + 1);
  };
  const goBack = () => {
    telemetry.emit(T.WIZARD_STEP_BACK, { step: active });
    if (active > 0) setActive(a => a - 1);
    else navigate("/");
  };
  // Stepper label click or a Review "Edit" button: a non-linear move.
  const jumpTo = (to: number) => {
    telemetry.emit(T.WIZARD_STEP_JUMPED, { from: active, to });
    setActive(to);
  };

  const stepProps = { onNext: goNext, onBack: goBack };

  return (
    <Container maxWidth={active >= 4 ? "lg" : "md"} sx={{ py: { xs: 3, sm: 5 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Create your pay stub
      </Typography>

      {isMobile ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Step {active + 1} of {STEPS.length}: {STEPS[active]}
          </Typography>
          <LinearProgress variant="determinate" value={((active + 1) / STEPS.length) * 100} sx={{ borderRadius: 1, height: 6 }} />
        </Box>
      ) : (
        <Stepper activeStep={active} sx={{ mb: 4 }}>
          {STEPS.map((label, i) => (
            <Step key={label} completed={i < active}>
              <StepLabel
                onClick={() => { if (i < active) jumpTo(i); }}
                sx={{ cursor: i < active ? "pointer" : "default" }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {active === 0 && <CompanyStep {...stepProps} />}
      {active === 1 && <EmployeeStep {...stepProps} />}
      {active === 2 && <PayPeriodStep {...stepProps} />}
      {active === 3 && <EarningsStep {...stepProps} />}
      {active === 4 && <TemplateStep {...stepProps} />}
      {active === 5 && <ReviewStep onBack={goBack} onEdit={jumpTo} />}
    </Container>
  );
};

export default PaystubWizard;
